import type { Language, Theme } from "@/types";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppContext } from "./AppContexts";

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { i18n } = useTranslation();

  const [theme, setTheme] = useState<Theme>(() => {
    const stored = localStorage.getItem("theme") as Theme | null;
    if (stored) return stored;
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  });

  // ✅ estado REACTIVO del idioma
  const [language, setLanguageState] = useState<Language>(
    (i18n.resolvedLanguage || i18n.language) as Language,
  );

  // ✅ escuchar cambios reales de i18next
  useEffect(() => {
    const onLanguageChanged = (lng: string) => {
      setLanguageState(lng as Language);
    };

    i18n.on("languageChanged", onLanguageChanged);

    return () => {
      i18n.off("languageChanged", onLanguageChanged);
    };
  }, [i18n]);

  // tema
  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove("light", "dark");
    root.classList.add(theme);
    localStorage.setItem("theme", theme);
  }, [theme]);

  const toggleTheme = useCallback(
    () => setTheme((prev) => (prev === "light" ? "dark" : "light")),
    [],
  );

  // ✅ cambio oficial
  const setLanguage = useCallback(
    async (lang: Language) => {
      if (i18n.language === lang) return;
      await i18n.changeLanguage(lang);
    },
    [i18n],
  );

  const value = useMemo(
    () => ({ theme, toggleTheme, language, setLanguage }),
    [theme, toggleTheme, language, setLanguage],
  );

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};
